/**
 * PINs equivocados, por persona y por equipo.
 *
 * A PIN is six digits, and a million tries by hand is out of reach — but somebody at the counter
 * who has watched the owner type it half-way, or who knows the owner uses a birthday, needs a
 * few dozen. Wrong tries went in the bitácora, and nothing slowed them down.
 *
 * Now the third wrong PIN in a row makes the pad wait before the next: 30 seconds, then twice as
 * long after every further miss, up to 15 minutes. The right PIN clears it. Kept per device,
 * since the wait is about the hands on this one, and in localStorage, so a reload does not reset
 * it. Somebody's own PIN counts under their name wherever it is typed — signing in, unlocking,
 * confirming a void — and the owner's PIN typed into an authorisation counts as the owner's.
 */

import { useEffect, useState } from 'react';
import type { useT } from '../i18n/index.js';

const CLAVE = 'abastece.intentosPin';
/** Wrong PINs allowed before the first wait. */
export const INTENTOS_LIBRES = 3;
const PRIMERA_ESPERA_MS = 30_000;
const ESPERA_MAXIMA_MS = 15 * 60_000;

export interface Intentos {
  fallos: number;
  /** Until when tries are refused, epoch ms; in the past once the wait is over. */
  hasta: number;
}

/** The record after one more wrong PIN at `ahora`. */
export function trasFallo(antes: Intentos | undefined, ahora: number): Intentos {
  const fallos = (antes?.fallos ?? 0) + 1;
  const espera =
    fallos < INTENTOS_LIBRES ? 0 : Math.min(PRIMERA_ESPERA_MS * 2 ** (fallos - INTENTOS_LIBRES), ESPERA_MAXIMA_MS);
  return { fallos, hasta: ahora + espera };
}

/** Whose tries these are: a person's own PIN, or the owner's typed into an authorisation. */
export type Quien = { usuarioId: string } | 'dueno';
const clave = (quien: Quien) => (quien === 'dueno' ? 'dueno' : `usuario:${quien.usuarioId}`);

function leer(): Record<string, Intentos> {
  try {
    return JSON.parse(localStorage.getItem(CLAVE) ?? '{}') as Record<string, Intentos>;
  } catch {
    return {};
  }
}

function escribir(todos: Record<string, Intentos>): void {
  try {
    localStorage.setItem(CLAVE, JSON.stringify(todos));
  } catch {
    /* A device that refuses storage gets no wait; the bitácora still has every try. */
  }
}

/** How long until the next try, in ms; 0 when it can be now. */
export function esperaDe(quien: Quien, ahora = Date.now()): number {
  return Math.max(0, (leer()[clave(quien)]?.hasta ?? 0) - ahora);
}

/** A wrong PIN. Returns how long the next try must wait, in ms. */
export function anotarFallo(quien: Quien, ahora = Date.now()): number {
  const todos = leer();
  const nuevo = trasFallo(todos[clave(quien)], ahora);
  escribir({ ...todos, [clave(quien)]: nuevo });
  return Math.max(0, nuevo.hasta - ahora);
}

/** The right PIN: the count starts again. */
export function anotarAcierto(quien: Quien): void {
  const todos = leer();
  if (!(clave(quien) in todos)) return;
  delete todos[clave(quien)];
  escribir(todos);
}

/**
 * A wait, counted down on screen: the seconds left (0 when none) and a way to start one. Ticks
 * once a second while there is something to count.
 */
export function useEspera(): [number, (ms: number) => void] {
  const [hasta, setHasta] = useState(0);
  const [, setTic] = useState(0);
  useEffect(() => {
    if (hasta <= Date.now()) return;
    const reloj = window.setInterval(() => {
      setTic((n) => n + 1);
      if (Date.now() >= hasta) window.clearInterval(reloj);
    }, 1000);
    return () => window.clearInterval(reloj);
  }, [hasta]);
  const restante = Math.max(0, Math.ceil((hasta - Date.now()) / 1000));
  return [restante, (ms: number) => setHasta(ms > 0 ? Date.now() + ms : 0)];
}

/** A wait as it is said: «30 s», «2 min». */
export function tiempoDeEspera(segundos: number, t: ReturnType<typeof useT>): string {
  return segundos < 60 ? t('pin.segundos', { n: segundos }) : t('pin.minutos', { n: Math.ceil(segundos / 60) });
}
